import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, X } from 'lucide-react'
import { toast } from 'sonner'

import { FormEvent, useEffect, useState } from 'react'

import {
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  PosButton,
  Segmented,
  Switch,
} from '@/components/ui'
import { PageHeader } from '@/components/pos/PageHeader'
import { NativeSelect } from '@/components/pos/office/NativeSelect'
import { OfficeTable } from '@/components/pos/office/OfficeTable'
import { useSession, useSyncState } from '@/contexts/SessionContext'
import { rowRef } from '@/db/repositories/catalog'
import { CatalogTable, listRows, saveBrand, saveCategory, saveProduct, saveUnit, saveVariation } from '@/db/repositories/catalogEdit'
import { useDebounce } from '@/hooks/ui/useDebounce'
import { nextInStoreCode } from '@/lib/inStoreCode'
import { PosSync } from '@/types'
import { formatMoney } from '@/utils'

type Row = PosSync.LocalRow
type Tab = 'products' | 'categories' | 'brands' | 'measurement_units'

const useRows = (table: CatalogTable) => {
  const sync = useSyncState()
  return useQuery({ queryKey: ['catalog-edit', table, sync.lastSyncedAt], queryFn: () => listRows(table) })
}

/** Waiting to sync, refused, or on the server: the same three states as orders (§15.8 U5, U6). */
const SyncBadge = ({ row }: { row: Row }) =>
  row.synced === 0 ? <span className="text-xs text-muted-foreground">waiting to sync</span> : row.synced === -1 ? <span className="text-xs text-destructive">needs attention</span> : null

const StatusBadge = ({ status }: { status: unknown }) => <Badge tone={(status ?? 'active') === 'active' ? 'success' : 'soft'}>{String(status ?? 'active')}</Badge>

/**
 * The product catalog, edited on this device and synced (POS.md §18.7 Q2, B19): products are business-wide, so an edit
 * here shows in every branch. Nothing is deleted from a till; switch a record off instead.
 */
export const CatalogPage = () => {
  const { can } = useSession()
  const tabs = (
    [
      ['products', 'Products', ['products:create', 'products:update']],
      ['categories', 'Categories', ['categories:create', 'categories:update']],
      ['brands', 'Brands', ['brands:create', 'brands:update']],
      ['measurement_units', 'Units', ['measurement_units:create', 'measurement_units:update']],
    ] as const
  ).filter(([, , keys]) => keys.some(can))
  const [tab, setTab] = useState<Tab>(tabs[0]?.[0] ?? 'products')

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Catalog" subtitle="Edits work offline and sync to every branch.">
        {tabs.length > 1 && <Segmented<Tab> value={tab} onChange={setTab} options={tabs.map(([value, label]) => ({ value, label }))} />}
      </PageHeader>
      {tab === 'products' ? <Products /> : <SimpleList table={tab} />}
    </div>
  )
}

const SIMPLE: Record<Exclude<Tab, 'products'>, { field: 'name' | 'title'; resource: string; noun: string; save: typeof saveCategory }> = {
  categories: { field: 'name', resource: 'categories', noun: 'category', save: saveCategory },
  brands: { field: 'name', resource: 'brands', noun: 'brand', save: saveBrand },
  measurement_units: {
    field: 'title',
    resource: 'measurement_units',
    noun: 'unit',
    save: (id, input) => saveUnit(id, { title: input.name, status: input.status }),
  },
}

/** Categories, brands and units: a name and on / off. */
const SimpleList = ({ table }: { table: Exclude<Tab, 'products'> }) => {
  const { can } = useSession()
  const client = useQueryClient()
  const def = SIMPLE[table]
  const { data = [], isLoading } = useRows(table)
  const [editing, setEditing] = useState<Row | 'new' | null>(null)
  const [name, setName] = useState('')
  const [active, setActive] = useState(true)

  useEffect(() => {
    if (!editing) return
    setName(editing === 'new' ? '' : String(editing[def.field] ?? ''))
    setActive(editing === 'new' || (editing.status ?? 'active') === 'active')
  }, [editing, def.field])

  const onSave = async (e: FormEvent) => {
    e.preventDefault()
    try {
      await def.save(editing === 'new' ? undefined : editing!.id, { name, status: active ? 'active' : 'inactive' })
      toast.success(`${name} saved`)
      setEditing(null)
      client.invalidateQueries({ queryKey: ['catalog-edit'] })
      client.invalidateQueries({ queryKey: ['categories'] })
      client.invalidateQueries({ queryKey: ['products'] })
    } catch (error) {
      toast.error((error as Error).message)
    }
  }

  const sorted = [...data].sort((a, b) => String(a[def.field]).localeCompare(String(b[def.field])))
  return (
    <div className="min-h-0 flex-1 space-y-3 overflow-auto p-4 md:p-6">
      {can(`${def.resource}:create`) && (
        <PosButton onClick={() => setEditing('new')}>
          <Plus /> New {def.noun}
        </PosButton>
      )}
      <OfficeTable
        cards
        loading={isLoading}
        rows={sorted}
        rowKey={(r) => r.id}
        onRowClick={can(`${def.resource}:update`) ? (r) => setEditing(r) : undefined}
        columns={[
          { label: 'Name', cell: (r) => <span className="font-medium text-ink">{String(r[def.field] ?? '')}</span> },
          { label: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
          { label: '', className: 'text-right', cell: (r) => <SyncBadge row={r} /> },
        ]}
      />
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent size="sm">
          <form onSubmit={onSave} className="space-y-4">
            <DialogHeader>
              <DialogTitle>{editing === 'new' ? `New ${def.noun}` : `Edit ${def.noun}`}</DialogTitle>
            </DialogHeader>
            <div className="space-y-1.5">
              <Label htmlFor="simple-name">Name</Label>
              <Input id="simple-name" autoFocus required value={name} onChange={(e) => setName(e.target.value)} className="h-11" />
            </div>
            <label className="flex items-center justify-between text-sm">
              Active
              <Switch checked={active} onCheckedChange={setActive} />
            </label>
            <DialogFooter>
              <PosButton type="button" variant="secondary" onClick={() => setEditing(null)}>
                Cancel
              </PosButton>
              <PosButton type="submit">Save</PosButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const options = (list: Row[], field: string, none: string) => [
  { value: '', label: none },
  ...list
    .filter((r) => (r.status ?? 'active') === 'active')
    .sort((a, b) => String(a[field]).localeCompare(String(b[field])))
    .map((r) => ({ value: rowRef(r), label: String(r[field] ?? '') })),
]

const Products = () => {
  const { can } = useSession()
  const { data: products = [], isLoading } = useRows('products')
  const { data: variations = [] } = useRows('product_variations')
  const { data: categories = [] } = useRows('categories')
  const [search, setSearch] = useState('')
  const debounced = useDebounce(search).trim().toLowerCase()
  const [editing, setEditing] = useState<Row | 'new' | null>(null)

  const variationsOf = (p: Row) =>
    variations.filter((v) => [p.id, p._id].filter(Boolean).map(String).includes(String(v.product_id ?? v.product_temp_id)))
  const categoryName = (p: Row) =>
    categories.find((c) => rowRef(c) === String(p.category_id ?? p.category_temp_id) || c.id === String(p.category_temp_id))?.name
  const shown = products
    .filter((p) => !debounced || `${p.name} ${variationsOf(p).map((v) => `${v.sku} ${(v.barcodes as string[] | undefined)?.join(' ') ?? ''}`).join(' ')}`.toLowerCase().includes(debounced))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)))

  return (
    <div className="min-h-0 flex-1 space-y-3 overflow-auto p-4 md:p-6">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, SKU or barcode" onCameraScan={setSearch} className="h-11 pl-9" />
        </div>
        {can('products:create') && can('product_variations:create') && (
          <PosButton onClick={() => setEditing('new')}>
            <Plus /> New product
          </PosButton>
        )}
      </div>
      <OfficeTable
        cards
        loading={isLoading}
        rows={shown}
        rowKey={(r) => r.id}
        onRowClick={(r) => setEditing(r)}
        columns={[
          { label: 'Product', cell: (p) => <span className="font-medium text-ink">{String(p.name ?? '')}</span> },
          { label: 'Category', className: 'hidden md:table-cell text-text-2', cell: (p) => String(categoryName(p) ?? '') },
          { label: 'Variations', className: 'text-right', cell: (p) => variationsOf(p).length },
          { label: 'Status', cell: (p) => <StatusBadge status={p.status} /> },
          { label: '', className: 'text-right', cell: (p) => <SyncBadge row={p} /> },
        ]}
      />
      {editing && <ProductDialog product={editing === 'new' ? null : editing} variations={editing === 'new' ? [] : variationsOf(editing)} onClose={() => setEditing(null)} />}
    </div>
  )
}

/** A product's own fields, then its variations; a new product is saved first, then gets its first variation. */
const ProductDialog = ({ product, variations, onClose }: { product: Row | null; variations: Row[]; onClose: () => void }) => {
  const { can } = useSession()
  const client = useQueryClient()
  const { data: categories = [] } = useRows('categories')
  const { data: brands = [] } = useRows('brands')
  const { data: units = [] } = useRows('measurement_units')
  const [savedId, setSavedId] = useState<string | undefined>(product?.id)
  const [variation, setVariation] = useState<Row | 'new' | null>(null)
  const editable = savedId ? can('products:update') : can('products:create')

  const onSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    try {
      const id = await saveProduct(savedId, {
        name: String(f.get('name')),
        category_id: String(f.get('category_id') || '') || null,
        brand_id: String(f.get('brand_id') || '') || null,
        measurement_unit_id: String(f.get('measurement_unit_id') || '') || null,
        sale_vat: parseFloat(String(f.get('sale_vat'))) || 0,
        price_type: f.get('price_type') === 'variable' ? 'variable' : 'fixed',
        sold_by_unit: f.get('sold_by_unit') === 'on',
        status: f.get('status') === 'on' ? 'active' : 'inactive',
      })
      toast.success('Product saved')
      client.invalidateQueries({ queryKey: ['catalog-edit'] })
      client.invalidateQueries({ queryKey: ['products'] })
      if (!savedId) {
        setSavedId(id)
        setVariation('new')
      }
    } catch (error) {
      toast.error((error as Error).message)
    }
  }

  const ref = (id: unknown, temp: unknown) => String(id ?? temp ?? '')
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{product ? String(product.name) : 'New product'}</DialogTitle>
          <DialogDescription>Products are shared by every branch. Prices per branch are under Prices.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSave} className="grid gap-3 sm:grid-cols-2">
          <fieldset disabled={!editable} className="contents">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="p-name">Name</Label>
              <Input id="p-name" name="name" required defaultValue={String(product?.name ?? '')} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-cat">Category</Label>
              <NativeSelect id="p-cat" name="category_id" defaultValue={ref(product?.category_id, product?.category_temp_id)} options={options(categories, 'name', 'No category')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-brand">Brand</Label>
              <NativeSelect id="p-brand" name="brand_id" defaultValue={ref(product?.brand_id, product?.brand_temp_id)} options={options(brands, 'name', 'No brand')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-unit">Unit</Label>
              <NativeSelect
                id="p-unit"
                name="measurement_unit_id"
                defaultValue={ref(product?.measurement_unit_id, product?.measurement_unit_temp_id)}
                options={options(units, 'title', 'Piece')}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-vat">VAT %</Label>
              <Input id="p-vat" name="sale_vat" inputMode="decimal" defaultValue={String(product?.sale_vat ?? 0)} className="h-11 text-right" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-price-type">Price at the POS</Label>
              <NativeSelect
                id="p-price-type"
                name="price_type"
                defaultValue={product?.price_type === 'variable' ? 'variable' : 'fixed'}
                options={[
                  { value: 'fixed', label: 'Fixed' },
                  { value: 'variable', label: 'Cashier enters' },
                ]}
              />
            </div>
            <div className="flex items-end gap-6 pb-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" name="sold_by_unit" defaultChecked={Boolean(product?.sold_by_unit)} className="size-4 accent-primary" />
                Sold by weight / length
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" name="status" defaultChecked={(product?.status ?? 'active') === 'active'} className="size-4 accent-primary" />
                Active
              </label>
            </div>
            {editable && (
              <div className="flex justify-end sm:col-span-2">
                <PosButton type="submit">{savedId ? 'Save product' : 'Save and add a variation'}</PosButton>
              </div>
            )}
          </fieldset>
        </form>

        {savedId && (
          <div className="space-y-2 border-t border-border pt-4">
            <div className="flex items-center justify-between">
              <p className="text-heading-md text-ink">Variations</p>
              {can('product_variations:create') && (
                <PosButton variant="outline" onClick={() => setVariation('new')}>
                  <Plus /> Add variation
                </PosButton>
              )}
            </div>
            <OfficeTable
              cards
              rows={variations}
              rowKey={(v) => v.id}
              empty="No variations yet."
              onRowClick={can('product_variations:update') ? (r) => setVariation(r) : undefined}
              columns={[
                { label: 'Name', cell: (v) => String(v.name ?? '—') },
                { label: 'SKU', className: 'font-mono text-xs', cell: (v) => String(v.sku ?? '') },
                { label: 'Price', className: 'text-right tabular-nums', cell: (v) => formatMoney(Number(v.regular_price ?? 0)) },
                { label: 'PLU', className: 'font-mono text-xs', cell: (v) => <PluCell row={v} /> },
                { label: '', className: 'text-right', cell: (v) => <SyncBadge row={v} /> },
              ]}
            />
          </div>
        )}
        {variation && savedId && <VariationDialog productId={savedId} variation={variation === 'new' ? null : variation} onClose={() => setVariation(null)} />}
      </DialogContent>
    </Dialog>
  )
}

/** `plu_code` is the server's (POS.md §18.8): a variation made offline gets one on its first sync. */
const PluCell = ({ row }: { row: Row }) =>
  row.plu_code ? <>{String(row.plu_code)}</> : row.synced !== 1 ? <span className="text-muted-foreground">PLU after sync</span> : <span className="text-muted-foreground">—</span>

/** A variation: price, SKU, every barcode that scans to it (`barcodes[]`), the in-store `20` code on demand. */
const VariationDialog = ({ productId, variation, onClose }: { productId: string; variation: Row | null; onClose: () => void }) => {
  const client = useQueryClient()
  const [barcodes, setBarcodes] = useState<string[]>(Array.isArray(variation?.barcodes) ? variation!.barcodes.map(String) : [])
  const [code, setCode] = useState('')
  const [sku, setSku] = useState(String(variation?.sku ?? ''))

  const addCode = (value: string) => {
    const v = value.trim()
    if (v && !barcodes.includes(v)) setBarcodes([...barcodes, v])
    setCode('')
  }

  const generate = async () => {
    try {
      const next = await nextInStoreCode('20')
      setBarcodes((list) => [...list, next])
      if (!sku) setSku(next)
    } catch (error) {
      toast.error((error as Error).message)
    }
  }

  const onSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    try {
      await saveVariation(variation?.id, {
        product_id: productId,
        name: String(f.get('name') || ''),
        sku: sku || barcodes[0] || '',
        regular_price: parseFloat(String(f.get('regular_price'))) || 0,
        min_stock_quantity: parseFloat(String(f.get('min_stock_quantity'))) || 0,
        barcodes: code.trim() ? [...barcodes, code.trim()] : barcodes,
        status: f.get('status') === 'on' ? 'active' : 'inactive',
      })
      toast.success('Variation saved')
      client.invalidateQueries({ queryKey: ['catalog-edit'] })
      client.invalidateQueries({ queryKey: ['products'] })
      onClose()
    } catch (error) {
      toast.error((error as Error).message)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm">
        <form onSubmit={onSave} className="space-y-3">
          <DialogHeader>
            <DialogTitle>{variation ? 'Edit variation' : 'New variation'}</DialogTitle>
            <DialogDescription>
              PLU: {variation?.plu_code ? String(variation.plu_code) : variation?.synced === 1 ? 'none' : 'given by the server after sync'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="v-name">Name (optional)</Label>
            <Input id="v-name" name="name" defaultValue={String(variation?.name ?? '')} placeholder="e.g. 1 kg" className="h-11" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="v-price">Price</Label>
              <Input id="v-price" name="regular_price" inputMode="decimal" required defaultValue={String(variation?.regular_price ?? '')} className="h-11 text-right" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-min">Reorder level</Label>
              <Input id="v-min" name="min_stock_quantity" inputMode="decimal" defaultValue={String(variation?.min_stock_quantity ?? 0)} className="h-11 text-right" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="v-sku">SKU</Label>
            <Input id="v-sku" value={sku} onChange={(e) => setSku(e.target.value)} placeholder="Defaults to the first barcode" className="h-11" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="v-code">Barcodes</Label>
            <div className="flex flex-wrap gap-1">
              {barcodes.map((b) => (
                <Badge key={b} tone="outline" className="gap-1 font-mono">
                  {b}
                  <button type="button" aria-label={`Remove ${b}`} onClick={() => setBarcodes(barcodes.filter((x) => x !== b))}>
                    <X className="size-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                id="v-code"
                value={code}
                placeholder="Scan or type a barcode"
                onCameraScan={addCode}
                continuousScan
                onChange={(e) => setCode(e.target.value)}
                // A scanner ends with Enter; it adds the code instead of submitting.
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCode(code))}
                className="h-11"
              />
              <PosButton type="button" variant="secondary" onClick={generate} title="In-store EAN-13 code (20…)">
                New 20 code
              </PosButton>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" name="status" defaultChecked={(variation?.status ?? 'active') === 'active'} className="size-4 accent-primary" />
            Active
          </label>
          <DialogFooter>
            <PosButton type="button" variant="secondary" onClick={onClose}>
              Cancel
            </PosButton>
            <PosButton type="submit">Save</PosButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
